<template>
    <div v-clickaway="closeOverlay" :class="detailsClass">
        <button type="button" class="card-contact-details__cta" @click="openOverlay()">
            <common-icon name="calendar" size="gigantic"></common-icon>

            <p>
                <span>{{ title }}</span>
                <em>{{ $t('elements.referrals.card.see_contact_details') }}</em>
            </p>
        </button>

        <div class="card-contact-details__overlay-wrapper">
            <section class="card-contact-details__overlay">
                <button type="button" class="card-contact-details__close" @click="closeOverlay()">
                    <common-icon name="close" size="small"></common-icon>
                </button>
                <a v-if="physicianEmail" class="card-contact-details__email" @click="sendMailToPhysician">
                    <common-content-spacer class="card-contact-details__overlay--container" height="small">
                        <common-icon-info
                            v-if="getLocationInformation('address')"
                            class="card-contact-details__address"
                            icon="location"
                            size="extra-huge"
                        >
                            <div>
                                <p>{{ getLocationInformation('address') }}</p>
                                <p>
                                    {{
                                        `${getLocationInformation('city')},
                                    ${getLocationInformation('state')} ${getLocationInformation('zip')}`
                                    }}
                                </p>
                                <p>{{ physicianPhone }}</p>
                            </div>
                        </common-icon-info>

                        <template v-if="!ongoingBackgroundCheck">
                            <p v-if="physician.isRecord || !physician.practice" class="card-contact-details__overlay-title">
                                {{ cardMessageByJoinStatus }}
                            </p>
                            <p
                                v-if="(physician.practice || physicianRegistrationNotCompleted) && !emailValidationReminderSent"
                                class="card-contact-details__send-reminder"
                            >
                                {{ $t('elements.referrals.card.send_reminder') }}
                            </p>
                            <div v-if="emailValidationReminderSent" class="success-notification">
                                <common-icon name="success" size="small"></common-icon>
                                <p>{{ $t('elements.referrals.card.reminder_sent') }}</p>
                            </div>

                            <common-icon-info v-else-if="physicianNotInvited" icon="calendar-blue" size="huge">
                                <p class="card-contact-details__email--field">{{ physicianEmail }}</p>
                            </common-icon-info>
                            <common-icon-info v-else-if="physicianInvited && reminderLink" icon="calendar-blue" size="huge">
                                <p class="card-contact-details__email--field">
                                    {{ $t('common.microcopy.reinvite') }}
                                </p>
                            </common-icon-info>
                        </template>
                    </common-content-spacer>
                </a>
            </section>
        </div>
    </div>
</template>

<script>
    import PhysiciansAPI from 'api/PhysiciansAPI'
    import UsersAPI from 'api/UsersAPI'
    import UserRolesEnum from '@/enums/UserRolesEnum'
    import UserJoinStatusEnum from '@/enums/UserJoinStatusEnum'

    export default {
        props: {
            physician: { type: Object, required: true },
            locations: { type: Array, default: null },
        },

        data() {
            return {
                physicianEmail: this.physician.email,
                physicianPhone: this.physician.phone,
                isOverlayOpen: false,
                joinStatus: 0,
                reminderLink: null,
                ongoingBackgroundCheck: false,
                emailValidationReminderSent: false,
            }
        },

        computed: {
            physicianNotInvited() {
                return this.joinStatus === UserJoinStatusEnum.NOT_INVITED
            },
            physicianInvited() {
                return this.joinStatus === UserJoinStatusEnum.INVITED
            },
            physicianRegistrationNotCompleted() {
                return this.joinStatus === UserJoinStatusEnum.REGISTRATION_NOT_COMPLETED
            },

            cardMessageByJoinStatus() {
                switch (this.joinStatus) {
                    case UserJoinStatusEnum.PROCESSING:
                        return this.$t('elements.referrals.card.user_being_invited', { name: this.physicianName })
                    case UserJoinStatusEnum.INVITED:
                        return this.$t('elements.referrals.card.user_already_invited', { name: this.physicianName })
                    case UserJoinStatusEnum.NOT_INVITED:
                        return this.$t('elements.referrals.card.invite_user_to_join', { name: this.physicianName })
                    case UserJoinStatusEnum.REGISTRATION_NOT_COMPLETED:
                        return this.$t('elements.referrals.card.registration_not_completed', { name: this.physicianName })
                    default:
                        return this.$t('elements.referrals.card.registration_completed', { name: this.physicianName })
                }
            },

            detailsClass() {
                return {
                    'card-contact-details': true,
                    'card-contact-details--is-overlay-open': this.isOverlayOpen,
                }
            },

            title() {
                if (this.physician.isRecord) {
                    return this.$t('elements.referrals.card.not_medmatch_member')
                }
                return this.isScheduledSet
                    ? this.$t('elements.referrals.card.email_verification_pending')
                    : this.$t('elements.referrals.card.schedule_not_set')
            },

            isScheduledSet() {
                return !!this.locations.length
            },

            physicianName() {
                return this.getMemberName(this.physician)
            },
        },

        methods: {
            openOverlay() {
                if (!this.$auth.getActiveMember() && !this.$auth.isUserPracticeOther()) {
                    this.emitEvent({
                        name: 'go-to-registration',
                    })
                    return
                }
                if (!this.physician.email || !this.physician.phone) {
                    this.getPhysicianData()
                }
                this.isOverlayOpen = true
            },

            getMemberName(member) {
                if (!member) return ''
                if (member.name) return member.name
                const name = `${member.firstName || ''} ${member.lastName || ''}`.trim()
                const designation = member.designation || member.title || ''
                return designation ? `${name}, ${designation}` : name
            },

            closeOverlay() {
                this.isOverlayOpen = false
            },

            getPhysicianData() {
                if (!this.physician.email || !this.physician.phone) {
                    return PhysiciansAPI.getPhysicianData({
                        userId: this.$auth.getActiveMember().id,
                        physicianId: this.physician.id,
                    }).then((res) => {
                        this.physicianEmail = res.email
                        this.physicianPhone = res.phone
                    })
                } else {
                    return Promise.resolve()
                }
            },

            getLocationInformation(field) {
                if (this.locations && this.locations.length) {
                    return this.locations[0][field]
                }
                return this.physician[field]
            },

            sendMailToPhysician(clickEvent) {
                if (clickEvent && clickEvent.preventDefault) {
                    clickEvent.preventDefault()
                }
                if (this.ongoingBackgroundCheck) {
                    return
                }
                if (
                    !this.physician.isRecord ||
                    this.physician.practice ||
                    this.joinStatus === UserJoinStatusEnum.REGISTRATION_NOT_COMPLETED
                ) {
                    UsersAPI.sendVerificationReminderEmail(this.$auth.getActiveMember().id, this.physician.id).then(() => {
                        this.emailValidationReminderSent = true
                    })
                } else if (this.joinStatus === UserJoinStatusEnum.NOT_INVITED) {
                    this.joinStatus = UserJoinStatusEnum.PROCESSING
                    const practiceType = this.$auth.getPractice().practiceType
                    UsersAPI.createJoinNetworkInvites(
                        this.$auth.getActiveMember().id,
                        [this.physicianEmail],
                        practiceType,
                        practiceType === 'physician' ? UserRolesEnum.PHYSICIAN.ID : null,
                    ).then(() => {
                        this.$set(this, 'joinStatus', UserJoinStatusEnum.INVITED)
                    })
                } else if (this.joinStatus === UserJoinStatusEnum.INVITED) {
                    if (this.reminderLink) {
                        window.open(
                            this.$t('native_emails.reinvite', {
                                recepientEmail: this.physicianEmail,
                                link: encodeURIComponent(this.reminderLink),
                            }),
                            '_blank',
                        )
                    }
                }
            },

            checkIfRecordIsAlreadyInvited() {
                if (this.physician.isRecord || !this.physician.practice) {
                    this.ongoingBackgroundCheck = true
                    this.getPhysicianData()
                        .then(() => {
                            return UsersAPI.checkJoinInvitesEmails(
                                this.$auth.getActiveMember().id,
                                [{ email: this.physicianEmail }],
                                this.$auth.getPractice().practiceType,
                            ).then((res) => {
                                const checks = res.emailsCheck
                                let value = -1
                                let furtherTask = null
                                if (checks.invited.length) {
                                    value = UserJoinStatusEnum.INVITED
                                    furtherTask = UsersAPI.getJoinInviteLink(
                                        this.physicianEmail,
                                        this.$auth.getActiveMember().id,
                                    ).then((res) => {
                                        this.reminderLink = res.link
                                    })
                                } else if (checks.newInvitations.length) {
                                    value = UserJoinStatusEnum.NOT_INVITED
                                } else if (checks.registeredInvites.length || checks.registeredOther.length) {
                                    value = UserJoinStatusEnum.REGISTRATION_COMPLETED
                                } else if (checks.registrationNotCompleted.length) {
                                    value = UserJoinStatusEnum.REGISTRATION_NOT_COMPLETED
                                }
                                this.$set(this, 'joinStatus', value)
                                return furtherTask
                            })
                        })
                        .finally(() => {
                            this.ongoingBackgroundCheck = false
                        })
                }
            },
        },

        created() {
            if (this.$auth.getActiveMember()) {
                this.checkIfRecordIsAlreadyInvited()
            }
        },
    }
</script>
